import React, { useState, ChangeEvent, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { Grid, Box, Typography, CircularProgress } from '@mui/material';
import {
  ImageContainer,
  FormContainer,
  StyledButton,
  StyledTextField,
  GridContainer,
} from './OTPVerification.styles';
import BackGround from '../../../../../Assets/AuthBackGround/AuthBG.png';
import { getDecryptValue } from '../../../SignIn/ChangePassword/decrypt';
import { sendOtp, validateOtp } from '../candidatePreboardingApi';
import { useSnackbar } from '../../../../../Context/SnackbarContext';

const OTPVerification: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [otpValues, setOtpValues] = useState<string[]>(Array(6).fill(''));
  const { tenant } = useParams<{ tenant: string }>();
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate();
  // Ref to track OTP sending status (not causing re-renders)
  const otpSentRef = useRef(false);
  const candidateRef = useRef({
    mapID: 0,
    email: '',
    num: 0,
    name: '',
    jobNum: 0,
    preboardingLinkUUID: '',
    expiryTime: 0,
    preboardingStatus: ''
  });
  const { showSnackbar } = useSnackbar();

  // 🔹 Load session
  const sessionData = sessionStorage.getItem("candidate_session");
  const session = sessionData ? JSON.parse(sessionData) : null;

  // 🔹 Helper to decrypt link safely
  const decryptCandidateFromUrl = () => {
    const encryptedData = searchParams.get("sxormoh5");
    if (!encryptedData) return null;
    try {
      const encryptedSegments = encryptedData.split(":");
      const decryptedSegments = encryptedSegments.map(seg => getDecryptValue(seg));
      console.log(decryptedSegments);
      return {
        mapID: parseInt(decryptedSegments[0], 10),
        email: decryptedSegments[1],
        num: parseInt(decryptedSegments[2], 10),
        name: decryptedSegments[3],
        jobNum: parseInt(decryptedSegments[4], 10),
        preboardingLinkUUID: decryptedSegments[5],
        expiryTime: parseInt(decryptedSegments[6], 10),
        preboardingStatus: decryptedSegments[7]
      };
    } catch (err) {
      console.error("Failed to decrypt candidate link", err);
      return null;
    }
  };

  useEffect(() => {
    const currentTime = Math.floor(Date.now() / 1000);
    const incomingCandidate = decryptCandidateFromUrl();

    if (!incomingCandidate) {
      setLoading(false);
      return;
    }

    // 🔹 Expiry check
    if (incomingCandidate.expiryTime && currentTime > incomingCandidate.expiryTime) {

      showSnackbar("The link has expired", "error");
      navigate(`/${tenant}/LinkExpired`);
      sessionStorage.removeItem("candidate_session");
      return;
    }

    // 🔹 If session exists
    if (session && session.candidate_access_token) {
      const isSameCandidate =
        session.mapID === incomingCandidate.mapID &&
        session.candidateNum === incomingCandidate.num &&
        session.jobNum === incomingCandidate.jobNum;

      if (isSameCandidate) {
        // ✅ Candidate matches → skip OTP
        candidateRef.current = incomingCandidate;
        setLoading(false);
        if (["Hired", "BGVRejected"].includes(incomingCandidate.preboardingStatus)) {
          navigate(`/${tenant}/preboarding/all-candidates/candidate-preboarding/hiring-experience`, { state: { mapID: incomingCandidate.mapID, jobNum: incomingCandidate.jobNum, candidateNum: incomingCandidate.num, candidateName: incomingCandidate.name } });
        } else {
          navigate(`/${tenant}/preboarding/all-candidates/candidate-preboarding/candidate-offer`, {
            state: {
              mapID: incomingCandidate.mapID,
              jobNum: incomingCandidate.jobNum,
              candidateNum: incomingCandidate.num,
              candidateName: incomingCandidate.name,
            },
          });
        }

        return;
      } else {
        // ❌ Different candidate → clear session and trigger OTP
        sessionStorage.removeItem("candidate_session");
      }
    }

    // 🔹 No session or session mismatch → OTP flow
    candidateRef.current = incomingCandidate;
    validateAndSendOtp().finally(() => setLoading(false));
  }, [searchParams, tenant]);


  const validateAndSendOtp = async () => {
    if (candidateRef.current.expiryTime !== null) { // Check if expiryTime is not null
      const currentTime = Math.floor(Date.now() / 1000);
      if (currentTime > candidateRef.current.expiryTime) {
        showSnackbar("The link has expired", "error")
        navigate(`/${tenant}/LinkExpired`);
        sessionStorage.removeItem("candidate_session");
        return;
      }
    }
    const payLoad = {
      "CandidateNum": candidateRef.current.num,
      "JobNum": candidateRef.current.jobNum,
      "CandidateName": candidateRef.current.name,
      "EmailID": candidateRef.current.email,
      "MapID": candidateRef.current.mapID,
      "PreboardingLinkUUID": candidateRef.current.preboardingLinkUUID
    }
    try {
      const response = await sendOtp(tenant, payLoad);

      if (response) {
        if (response.Status) {
          otpSentRef.current = true;
          showSnackbar(response.Message, 'success');
          return;
        } else {
          showSnackbar(response.Message, 'error');
          navigate(`/${tenant}/LinkExpired`);
          sessionStorage.removeItem("candidate_session");
          return;
        }
      }
    } catch (error) {
      console.log(error)
      showSnackbar('Failed to send OTP. Please try again.', 'error');
    }
  }


  const handleOtpChange = (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (/^\d*$/.test(value) && value.length <= 1) {
      const newOtpValues = [...otpValues];
      newOtpValues[index] = value;
      setOtpValues(newOtpValues);

      // Move focus to next input if a value is entered
      if (value && index < otpValues.length - 1) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        nextInput?.focus();
      }
    }
  };

  const handleOtpPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pasteData = event.clipboardData.getData('Text').replace(/\D/g, ''); // Only digits
    if (pasteData.length > 0) {
      const newOtpValues = [...otpValues];
      for (let i = 0; i < otpValues.length; i++) {
        newOtpValues[i] = pasteData[i] || '';
      }
      setOtpValues(newOtpValues);

      // Focus last filled box
      const lastFilled = Math.min(pasteData.length - 1, otpValues.length - 1);
      if (lastFilled >= 0) {
        const lastInput = document.getElementById(`otp-${lastFilled}`);
        lastInput?.focus();
      }
      event.preventDefault();
    }
  };


  const handleOtpKeyDown = (index: number) => (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && otpValues[index] === '' && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handleSubmit = async () => {
    if (otpValues.some(val => val === '')) {
      showSnackbar('Please enter all 6 digits of the OTP.', 'error');
      return;
    }
    try {
      const payLoad = {
        MapID: candidateRef.current.mapID,
        OTP: otpValues.join(''),
      }
      const response = await validateOtp(tenant, payLoad)

      if (response) {
        if (response.Status) {
          // 🔹 Save full candidate session
          const newSession = {
            candidate_access_token: response.CandidateAccessToken,
            mapID: candidateRef.current.mapID,
            jobNum: candidateRef.current.jobNum,
            candidateNum: candidateRef.current.num,
            candidateName: candidateRef.current.name,
          };
          sessionStorage.setItem("candidate_session", JSON.stringify(newSession));
          localStorage.removeItem("access_token")

          if (["Hired", "BGVRejected"].includes(response.PreboardingStatus)) {
            navigate(`/${tenant}/preboarding/all-candidates/candidate-preboarding/hiring-experience`, { state: { mapID: candidateRef.current.mapID, jobNum: candidateRef.current.jobNum, candidateNum: candidateRef.current.num, candidateName: candidateRef.current.name } });
          } else {
            navigate(`/${tenant}/preboarding/all-candidates/candidate-preboarding/candidate-offer`, { state: { mapID: candidateRef.current.mapID, jobNum: candidateRef.current.jobNum, candidateNum: candidateRef.current.num, candidateName: candidateRef.current.name } });
          }
        } else {
          showSnackbar(response.Message, 'error');
        }
      }
    } catch (error) {
      showSnackbar('Failed to validate OTP. Please try again.', 'error');
      console.log(error)
    }
  }


  if (loading) {
    return (
      <Box sx={{
        height: '100vh', // Ensures full height
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        <CircularProgress disableShrink />
      </Box>
    );
  }

  return (
    <Grid container className="otp-verification-container" sx={{ height: '100vh', width: '100vw', overflow: 'hidden' }}>
      {/* Left side with image */}
      <Grid item xs={12} md={6} sx={{ width: '50vw', height: '100vh' }}>
        <ImageContainer
          sx={{
            backgroundImage: `url(${BackGround})`,
            height: '100vh',
            width: '100%',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      </Grid>

      {/* Right side with OTP form */}
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <FormContainer sx={{ width: '40vw', padding: '2vh 2vw' }}>
          <Box sx={{ width: '100%', ml: '0.56vw' }}>
            <Typography variant="h4">Verify OTP</Typography>
            <Typography variant="subtitle1" sx={{ marginBottom: '0.5vw' }}>
              A verification code has been sent to your email
            </Typography>
            <Typography variant="subtitle3">
              Mail: {candidateRef.current.email || 'No Email Provided'}
            </Typography>
          </Box>

          <GridContainer
            container
            spacing={2}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              ml: '0.56vw',
              mt: '1vw'
            }}
          >
            {otpValues.map((digit, index) => (
              <StyledTextField
                key={index}
                id={`otp-${index}`}
                value={digit}
                onChange={handleOtpChange(index)}
                onKeyDown={handleOtpKeyDown(index)}
                onPaste={handleOtpPaste}
                size="small"
                inputProps={{
                  maxLength: 1,
                  style: { textAlign: 'center' },
                }}
                sx={{ width: '3vw', Height: '3vw', fontSize: '1.8vh' }}
              />
            ))}
          </GridContainer>
          <StyledButton
            onClick={handleSubmit}
            variant="contained"
            fullWidth
            size="small"
            sx={{ fontSize: '2vh', marginBottom: '1vh' }}
          >
            Submit OTP
          </StyledButton>
        </FormContainer>
      </Grid>

    </Grid>


  );
};

export default OTPVerification;